<template>
  <div class="message-list bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full">
    <!-- Header with Close Button for Better Mobile Experience -->
    <div v-if="!hideHeader" class="flex justify-between items-center p-3 md:p-4 border-b border-gray-200 sticky top-0 bg-white z-20">
      <h3 class="text-lg font-semibold text-gray-900">{{ title }}</h3>
      <div class="flex items-center space-x-2">
        <button
          @click="fetchMessages"
          :disabled="loading"
          class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
        >
          <svg
            class="w-5 h-5"
            :class="{ 'animate-spin': loading }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
        <!-- Mobile-friendly close button -->
        <button
          @click="$emit('close')"
          class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Messages List - Adjust height to accommodate the textarea -->
    <div class="flex-1 p-3 md:p-4 overflow-y-auto message-container" 
      :style="{ maxHeight: calculateMessageContainerHeight() }"
      ref="messagesContainer">
      <div v-if="messages.length > 0" class="space-y-3">
        <div
          v-for="message in messages"
          :key="message.id"
          :class="[
            'p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md',
            !message.read ? 'bg-blue-50 border-blue-200 animate-pulse' : 'bg-gray-50 border-gray-200',
            message.source === 'PUBLIC' && props.cruiseId ? 'hover:bg-green-50 hover:border-green-300' : 'hover:bg-gray-100'
          ]"
          @click="handleMessageClick(message)"
        >
          <div class="flex items-start space-x-3">
            <!-- Message Icon -->
            <div class="relative flex-shrink-0">
              <div
                :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center text-white',
                  getMessageBgColor(message.source)
                ]"
              >
                <!-- User Icon -->
                <svg v-if="message.source === 'USER'" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                <!-- Duck Icon -->
                <svg v-else-if="message.source === 'PUBLIC'" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.5,5A6.5,6.5 0 0,0 2,11.5A6.5,6.5 0 0,0 8.5,18A6.5,6.5 0 0,0 15,11.5A6.5,6.5 0 0,0 8.5,5M10.5,11A1.5,1.5 0 0,1 9,12.5A1.5,1.5 0 0,1 7.5,11A1.5,1.5 0 0,1 9,9.5A1.5,1.5 0 0,1 10.5,11M21.71,20.29L20.29,21.71L17.5,18.92L16.09,20.33L14.67,18.91L18.5,15.08L21.71,18.29L20.29,19.71L21.71,21.12L20.29,22.54L18.88,21.12L17.46,22.54L16.04,21.12L17.46,19.71L16.04,18.29L17.46,16.88L18.88,18.29L20.29,16.88L21.71,18.29M8.5,6.5A5,5 0 0,1 13.5,11.5A5,5 0 0,1 8.5,16.5A5,5 0 0,1 3.5,11.5A5,5 0 0,1 8.5,6.5Z"/>
                </svg>
                <!-- System Icon -->
                <svg v-else-if="message.source === 'SYSTEM'" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                </svg>
                <!-- Default Icon -->
                <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z"/>
                </svg>
              </div>
              <div
                v-if="!message.read"
                class="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"
              ></div>
            </div>

            <div class="flex-1 min-w-0">
              <!-- Message Header -->
              <div class="flex items-center justify-between mb-1">
                <div class="flex items-center space-x-2">
                  <span
                    :class="[
                      'font-semibold text-sm',
                      message.source === 'PUBLIC' ? 'text-green-700' : 'text-gray-900'
                    ]"
                  >
                    {{ getSenderName(message.source, message.duckId) }}
                  </span>
                  
                  <!-- New Message Badge -->
                  <span
                    v-if="!message.read"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 animate-bounce"
                  >
                    New!
                  </span>
                  
                  <!-- Action Badge for Duck Messages -->
                  <span
                    v-if="message.source === 'PUBLIC' && props.cruiseId"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200"
                  >
                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View
                  </span>
                </div>
                
                <span class="text-xs text-gray-500">{{ formatDate(message.timestamp) }}</span>
              </div>

              <!-- Message Content -->
              <p class="text-sm text-gray-700 break-words">{{ message.content }}</p>
              
              <!-- Action Hint for Duck Messages -->
              <div
                v-if="message.source === 'PUBLIC' && props.cruiseId"
                class="flex items-center mt-2 text-xs text-green-600"
              >
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                Click to open conversation
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Loading State -->
      <div v-else-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
      
      <!-- Empty State -->
      <div v-else class="text-center py-12">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <svg class="w-12 h-12 text-blue-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.5a2.5 2.5 0 00-2.5 2.5v1.5a2.5 2.5 0 002.5 2.5H20" />
          </svg>
          <p class="text-blue-700 font-medium">No messages found.</p>
        </div>
      </div>
    </div>

    <!-- Reply Section - Make it sticky at the bottom -->
    <div v-if="props.duckUuid" class="border-t border-gray-200 p-3 md:p-4 bg-gray-50 sticky-reply-section" 
      :class="{'keyboard-active': isKeyboardVisible}"
      ref="replySection">
      <div class="flex space-x-3">
        <div class="flex-1">
          <textarea
            v-model="replyMessage"
            placeholder="Type your reply..."
            rows="2"
            :disabled="loading"
            @focus="handleTextareaFocus"
            @blur="handleTextareaBlur"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-100"
            style="font-size: 16px;"
          ></textarea>
        </div>
        <button
          @click="sendMessage"
          :disabled="!replyMessage.trim() || sendingMessage"
          class="flex-shrink-0 w-10 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center transition-colors"
        >
          <svg
            v-if="sendingMessage"
            class="w-4 h-4 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <svg
            v-else
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useDemoMessageStore } from '@/stores/demoMessageStore';
import type { Message } from '@/interfaces/message';

const props = defineProps({
  cruiseId: {
    type: Number,
    default: null
  },
  duckUuid: {
    type: String,
    default: null
  },
  title: {
    type: String,
    default: 'Messages'
  },
  hideHeader: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update', 'close']);

const messageStore = useDemoMessageStore();
const messages = ref<Message[]>([]);
const loading = ref(false);
const sendingMessage = ref(false);
const replyMessage = ref(''); // Ensure replyMessage is initialized as a string
const isKeyboardVisible = ref(false);
const initialWindowHeight = ref(window.innerHeight);
const messagesContainer = ref<HTMLElement | null>(null);
const replySection = ref(null);
const lastScrollPosition = ref(0);

onMounted(() => {
  fetchMessages();
  
  // Add event listeners for detecting keyboard visibility
  window.addEventListener('resize', handleWindowResize);
  
  // Store initial window height for comparison
  initialWindowHeight.value = window.innerHeight;
  
  // Add viewport change detection for mobile browsers
  if ('visualViewport' in window && window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportResize);
  }
});

onUnmounted(() => {
  // Remove event listeners when component is unmounted
  window.removeEventListener('resize', handleWindowResize);
  
  if ('visualViewport' in window && window.visualViewport) {
    window.visualViewport.removeEventListener('resize', handleViewportResize);
  }
});

const fetchMessages = async () => {
  try {
    loading.value = true;
    
    if (props.cruiseId) {
      // Fetch messages for a cruise
      messages.value = messageStore.getCruiseMessages(props.cruiseId);
      // Mark messages as read
      messageStore.markAsRead(`cruise-${props.cruiseId}`);
      // Notify parent component to update notification counts
      emit('update', { cruiseId: props.cruiseId, clearAll: true });
    } else if (props.duckUuid) {
      // Fetch messages for a duck
      messages.value = messageStore.getDuckMessages(props.duckUuid);
      // Mark messages as read
      messageStore.markAsRead(props.duckUuid);
      // Notify parent component to update notification counts
      emit('update', { duckUuid: props.duckUuid, refreshCruise: true });
    }
  } catch (error) {
    console.error('Failed to fetch messages:', error);
  } finally {
    loading.value = false;
  }
};

const sendMessage = async () => {
  if (!props.duckUuid || !replyMessage.value.trim()) return; // Use replyMessage.value.trim()

  try {
    sendingMessage.value = true;
    
    // Add message to the duck
    messageStore.addDuckMessage(props.duckUuid, replyMessage.value.trim(), 'USER');
    
    // Clear the input and refresh messages
    replyMessage.value = ''; // Reset replyMessage to an empty string
    await fetchMessages();
  } catch (error) {
    console.error('Failed to send message:', error);
  } finally {
    sendingMessage.value = false;
  }
};

// Method to handle when a user clicks on a message
const handleMessageClick = (message: Message) => {
  // Add a visual feedback when clicking
  const listItems = document.querySelectorAll('.v-list-item');
  const clickedItem = Array.from(listItems).find(item => 
    item.classList.contains(`message-${message.id}`)
  );
  
  if (clickedItem) {
    clickedItem.classList.add('clicked');
    setTimeout(() => clickedItem.classList.remove('clicked'), 300);
  }
   
  // If it's a message from a duck and we're in cruise view, open the duck messages
  if (message.source === 'PUBLIC' && props.cruiseId && message.duckUuid) {
    // Add a brief delay for the visual feedback before navigating
    setTimeout(() => {
      // Tell the parent component to open the duck-specific messages
      emit('update', { 
        duckUuid: message.duckUuid, 
        openDuckMessages: true,
        messageId: message.id
      });
    }, 150);
  }
};

const formatDate = (timestamp: string) => {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const getSenderName = (source: string, duckId?: number): string => {
  switch (source) {
    case 'USER':
      return 'Me';
    case 'SYSTEM':
      return 'System';
    case 'PUBLIC':
      return duckId ? `Duck ${duckId}` : 'Anonymous';
    default:
      return 'Unknown';
  }
};

const getMessageBgColor = (source: string): string => {
  switch (source) {
    case 'USER':
      return 'bg-blue-600';
    case 'PUBLIC':
      return 'bg-green-600';
    case 'SYSTEM':
      return 'bg-orange-600';
    default:
      return 'bg-gray-600';
  }
};

// Add this function to calculate the appropriate height for the message container
const calculateMessageContainerHeight = () => {
  if (window.innerWidth <= 640) {
    if (isKeyboardVisible.value) {
      // When keyboard is visible, ensure almost no space is wasted
      return props.duckUuid ? 
        'calc(60vh - 120px)' : // Reduced height to minimize gap when keyboard is open
        'calc(90vh - 80px)';
    }
    return props.duckUuid ? 
      'calc(85vh - 140px)' : // More screen space for duck messages
      'calc(90vh - 80px)';   // More screen space for cruise messages
  }
  
  // Desktop sizes
  const baseHeight = props.duckUuid ? 'calc(70vh - 160px)' : 'calc(100vh - 200px)';
  return props.duckUuid ? baseHeight : 'calc(70vh - 120px)';
};

// Mobile-specific methods to handle textarea focus
const handleTextareaFocus = () => {
  isKeyboardVisible.value = true;
  
  // Save current scroll position
  if (messagesContainer.value) {
    lastScrollPosition.value = messagesContainer.value.scrollTop;
  }
  
  // Immediately adjust layout for better visual appearance
  setTimeout(() => {
    if (replySection.value) {
      // Remove any additional margin/padding when keyboard is active
      document.body.classList.add('keyboard-open');
      
      // Wait for DOM update then scroll to keep the last messages visible
      nextTick(() => {
        // Scroll the textarea into view with minimal white space
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
      });
    }
  }, 10); // Very short timeout to ensure DOM is ready
};

const handleTextareaBlur = () => {
  document.body.classList.remove('keyboard-open');
  
  // Add slight delay before hiding keyboard indicator
  setTimeout(() => {
    isKeyboardVisible.value = false;
    
    // Restore scroll position when keyboard closes
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = lastScrollPosition.value;
    }
  }, 100);
};

// Handle visual viewport resize (for modern browsers)
const handleViewportResize = () => {
  if ('visualViewport' in window && window.visualViewport) {
    const isKeyboard = window.visualViewport.height < initialWindowHeight.value * 0.75;
    
    // Only act when keyboard state changes
    if (isKeyboard !== isKeyboardVisible.value) {
      isKeyboardVisible.value = isKeyboard;
      
      // When keyboard appears
      if (isKeyboard) {
        document.body.classList.add('keyboard-open');
        
        // Ensure reply section is fully visible with minimal gap
        nextTick(() => {
          if (messagesContainer.value) {
            // Scroll to the bottom of messages
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
          }
        });
      } else {
        document.body.classList.remove('keyboard-open');
      }
    }
  }
};

// Fall back to window resize for older browsers
const handleWindowResize = () => {
  // Only use this method if visualViewport is not available
  if (!('visualViewport' in window) && typeof window !== 'undefined') {
    const windowWithInnerHeight = window as Window & typeof globalThis;
    // If the window height is significantly smaller, the keyboard is probably showing
    isKeyboardVisible.value = windowWithInnerHeight.innerHeight < initialWindowHeight.value * 0.75;
  }
};
</script>

<style scoped>
/* Custom animations */
@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

@keyframes pulse {
  50% {
    opacity: .5;
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(-25%);
    animation-timing-function: cubic-bezier(0.8,0,1,1);
  }
  50% {
    transform: none;
    animation-timing-function: cubic-bezier(0,0,0.2,1);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-bounce {
  animation: bounce 1s infinite;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Ensure proper scrolling */
.message-list {
  height: 100%;
  max-height: 100%;
}

.message-list > div:nth-child(2) {
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 transparent;
}

.message-list > div:nth-child(2)::-webkit-scrollbar {
  width: 6px;
}

.message-list > div:nth-child(2)::-webkit-scrollbar-track {
  background: transparent;
}

.message-list > div:nth-child(2)::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.message-list > div:nth-child(2)::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Make sure reply section stays fixed at the bottom */
.sticky-reply-section {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: white;
  width: 100%;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
}

/* Enhanced mobile experience */
@media (max-width: 640px) {
  .message-list {
    height: 100vh;
    height: 100dvh; /* Use dynamic viewport height where supported */
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
    /* Use entire screen width on mobile */
    width: 100vw;
  }
  
  /* More screen space for the message container */
  .message-container {
    /* Adjusted to use more vertical space */
    max-height: calc(100vh - 140px) !important;
    max-height: calc(100dvh - 140px) !important;
  }
  
  /* Improved position for reply section */
  .sticky-reply-section.keyboard-active {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 50;
    margin-top: 0;
    /* Add iOS safe area insets for notched devices */
    padding-bottom: calc(env(safe-area-inset-bottom, 8px) + 8px);
    /* Remove animation to prevent unwanted space */
    transition: none;
  }
  
  /* Remove any gaps when keyboard is open */
  :global(body.keyboard-open) .message-container {
    padding-bottom: 0 !important;
    margin-bottom: 0 !important;
  }
  
  /* Create a tighter layout when keyboard is open */
  :global(body.keyboard-open) .sticky-reply-section.keyboard-active {
    border-top-width: 1px;
    box-shadow: none; /* Remove shadow to reduce perceived gap */
  }

  /* Special handling for iOS */
  @supports (-webkit-touch-callout: none) {
    .messages-container {
      -webkit-overflow-scrolling: touch;
      overflow: auto;
    }
    
    /* Optimize iOS keyboard appearance - eliminate extra space */
    :global(body.keyboard-open) {
      /* Prevent overscroll which creates gap */
      overflow: hidden;
      position: fixed;
      width: 100%;
      height: var(--window-height, 100%);
    }
    
    /* Remove gap between messages and input when keyboard is open */
    :global(body.keyboard-open) .message-container {
      /* Match height with available space */
      max-height: calc(100% - 120px) !important;
    }
  }
}

/* Focus states for accessibility */
button:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

textarea:focus {
  outline: none;
}

/* Improve mobile input experience */
@supports (-webkit-touch-callout: none) {
  /* iOS specific fixes */
  textarea {
    font-size: 16px; /* Prevents zoom on iOS */
    -webkit-appearance: none;
    appearance: none;
  }
}
</style>
